/* eslint-disable */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { CreditCard } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "./i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * PaymentProfileCard — inline profile section for stockers/NMs to set their
 * preferred payment method (Zelle / CashApp / Cash). When they later press
 * "Retire Lajan", this info is auto-attached to the WhatsApp message to CEO.
 */
const PaymentProfileCard = ({ userId }) => {
  const { t } = useLang();
  const [profile, setProfile] = useState({ method: "", account_id: "", account_name: "", phone: "" });
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!userId) return;
    axios.get(`${API}/user/${userId}/payment-profile`).then(r => {
      const pp = r.data.payment_profile || {};
      setProfile({
        method: pp.method || "",
        account_id: pp.account_id || "",
        account_name: pp.account_name || "",
        phone: pp.phone || "",
      });
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, [userId]);

  const save = async () => {
    if (!profile.method) {
      toast.error(t("pp_choose_method"));
      return;
    }
    if (profile.method !== "cash" && (!profile.account_id || !profile.account_name)) {
      toast.error(t("pp_fill_account"));
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/user/${userId}/payment-profile`, profile);
      toast.success(t("pp_saved"));
    } catch (e) {
      toast.error(t("pp_save_error"));
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return null;

  return (
    <div className="glass-panel p-4 mb-4" data-testid="payment-profile-card">
      <h4 className="text-white font-bold mb-3 flex items-center gap-2">
        <CreditCard className="w-5 h-5 text-emerald-400" /> {t("pp_active_method")}
      </h4>
      <p className="text-zinc-500 text-xs mb-3">{t("pp_whatsapp_hint")}</p>

      <div className="grid grid-cols-3 gap-2 mb-3">
        {[
          { id: "zelle", label: "Zelle", bg: "bg-purple-600", letter: "Z" },
          { id: "cashapp", label: "Cash App", bg: "bg-emerald-500", letter: "$" },
          { id: "cash", label: t("pp_cash_label"), bg: "bg-amber-500", letter: "💵" },
        ].map(m => (
          <button
            key={m.id}
            onClick={() => setProfile(p => ({ ...p, method: m.id }))}
            className={`flex flex-col items-center gap-1 rounded-xl p-3 border-2 transition-all ${
              profile.method === m.id
                ? "border-emerald-400 bg-emerald-950/40"
                : "border-zinc-700 hover:border-zinc-500 bg-zinc-900/60"
            }`}
            data-testid={`pp-method-${m.id}`}
          >
            <div className={`w-9 h-9 rounded-full ${m.bg} flex items-center justify-center text-white font-black text-lg`}>{m.letter}</div>
            <span className="text-white text-xs font-bold">{m.label}</span>
          </button>
        ))}
      </div>

      {profile.method && profile.method !== "cash" && (
        <div className="space-y-2 mb-3">
          <div>
            <label className="text-zinc-400 text-xs">{profile.method === "zelle" ? t("pp_zelle_id_label") : t("pp_cashapp_tag_label")}</label>
            <input
              type="text"
              value={profile.account_id}
              onChange={e => setProfile(p => ({ ...p, account_id: e.target.value }))}
              placeholder={profile.method === "zelle" ? "+1 555 123 4567" : "$YourTag"}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
              data-testid="pp-account-id"
            />
          </div>
          <div>
            <label className="text-zinc-400 text-xs">{t("pp_account_holder")}</label>
            <input
              type="text"
              value={profile.account_name}
              onChange={e => setProfile(p => ({ ...p, account_name: e.target.value }))}
              placeholder={t("pp_full_name")}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
              data-testid="pp-account-name"
            />
          </div>
        </div>
      )}

      <div className="mb-3">
        <label className="text-zinc-400 text-xs">{t("pp_phone_hint")}</label>
        <input
          type="tel"
          value={profile.phone}
          onChange={e => setProfile(p => ({ ...p, phone: e.target.value }))}
          placeholder="+509 3000 0000"
          className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
          data-testid="pp-phone"
        />
      </div>

      <button
        onClick={save}
        disabled={saving}
        className="w-full bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-bold py-2 rounded-lg text-sm flex items-center justify-center gap-2"
        data-testid="pp-save-btn"
      >
        {saving ? t("pp_saving") : `💾 ${t("pp_save_btn")}`}
      </button>
    </div>
  );
};

export default PaymentProfileCard;
